import { useTranslation } from "react-i18next";
import { Input } from "~/atoms/input";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import { Button } from "~/atoms/button";
import { createDocument } from "~/api/v2/requests";
import { toast } from "react-toastify";
import { useState } from "react";
import { RadioGroup, RadioGroupItem } from "~/atoms/radio-group";
import { type CategoryType, type TierDetails } from "~/api/types";
import { useNavigate } from "react-router";
import { featureFlags } from "~/config/featureFlags";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import i18n from "~/i18n";
import {
  MAX_FILE_SIZE_BYTES,
  UPLOAD_FILE_TYPES_ACCEPT,
} from "~/api/v2/constants";
import { useFeatureFlagEnabled } from "posthog-js/react";

const OfficeFormSchema = z
  .object({
    categoryId: z.string({ message: i18n.t("validations.required") }),
    tierId: z.string({ message: i18n.t("validations.required") }),
    source: z.enum(["new", "upload"]),
    type: z.string().optional(),
    file: z
      .file()
      .max(MAX_FILE_SIZE_BYTES, {
        message: i18n.t("commons.document.fileSizeLimit"),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.source === "new") {
      if (!data.type)
        ctx.addIssue({
          message: i18n.t("validations.required"),
          code: "custom",
          path: ["type"],
        });
    } else {
      if (!data.file)
        ctx.addIssue({
          message: i18n.t("validations.required"),
          code: "custom",
          path: ["file"],
        });
    }
  });

interface Props {
  categories?: CategoryType[];
  tiers?: TierDetails[];
}

const getOfficeFileTypes = [
  { value: "docx", label: i18n.t("commons.fileFormat.word") },
  { value: "xlsx", label: i18n.t("commons.fileFormat.excel") },
  { value: "pptx", label: i18n.t("commons.fileFormat.powerPoint") },
];

const testFileTypes = [{ value: "wopitest", label: "WopiTest (.wopitest)" }];

export function NewDocumentForm({ categories, tiers }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const officeFileTypes = getOfficeFileTypes;
  const showWopiTestFileType = useFeatureFlagEnabled(featureFlags.wopitest);

  const form = useForm({
    resolver: zodResolver(OfficeFormSchema),
    defaultValues: {
      source: "upload",
    },
  });

  const sourceWatch = useWatch({
    control: form.control,
    name: "source",
  });

  const fileTypes = [
    ...officeFileTypes,
    ...(showWopiTestFileType ? testFileTypes : []),
  ];

  const categoryOptions = categories?.map((dep) => ({
    label: dep.title,
    value: dep.id.toString(),
  }));

  const tierOptions = tiers?.map((tier) => ({
    label: tier.title,
    value: tier.id.toString(),
  }));

  async function onSubmit(data: z.infer<typeof OfficeFormSchema>) {
    try {
      setCreating(true);

      const formData = new FormData();
      const title = data.file?.name
        ? data.file.name.replace(/\.[^.]+$/, "")
        : t("commons.document.untitledDocument");
      formData.append("title", title);
      formData.append("categoryId", data.categoryId);
      formData.append("tierId", data.tierId);
      formData.append("fileType", extractFileType(data));
      if (data.file) formData.append("file", data.file);

      const { documentId } = await createDocument(formData);

      await navigate(`/documents/${documentId}`);
    } catch {
      toast.error(t("dashboard.createDocument.errorCreating"));
    } finally {
      setCreating(false);
    }
  }

  function extractFileType(data: z.infer<typeof OfficeFormSchema>): string {
    if (data.source === "new") {
      if (!data.type) throw new Error("File type is required");
      return data.type;
    }

    if (!data.file) throw new Error("File is required");
    const fileExtension = data.file.name.split(".").pop();
    if (!fileExtension) throw new Error("Invalid file extension");
    return fileExtension;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-3">
        <FormField
          control={form.control}
          name="categoryId"
          render={({ field }) => (
            <FormItem className="min-w-0">
              <FormLabel>{t("commons.document.category")}</FormLabel>
              <FormControl>
                <ControlledCombobox
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("commons.forms.selectAnOption")}
                  options={categoryOptions}
                  disabled={!categories}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="tierId"
          render={({ field }) => (
            <FormItem className="min-w-0">
              <FormLabel>{t("commons.document.tier")}</FormLabel>
              <FormControl>
                <ControlledCombobox
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("commons.forms.selectAnOption")}
                  options={tierOptions}
                  disabled={!tiers}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <hr className="my-2" />
        <FormField
          control={form.control}
          name="source"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <FormControl>
                      <RadioGroupItem value="upload" id="upload" />
                    </FormControl>
                    <FormLabel htmlFor="upload">
                      {t("dashboard.createDocument.uploadFile")}
                    </FormLabel>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FormControl>
                      <RadioGroupItem value="new" id="new" />
                    </FormControl>
                    <FormLabel htmlFor="new">
                      {t("commons.create.document")}
                    </FormLabel>
                  </div>
                </RadioGroup>
              </FormControl>
            </FormItem>
          )}
        />
        {sourceWatch === "upload" ? (
          <FormField
            control={form.control}
            name="file"
            render={({ field: { value: _value, onChange, ...fieldProps } }) => (
              <FormItem>
                <FormLabel>{t("dashboard.createDocument.file")}</FormLabel>
                <FormControl>
                  <div className="grid w-full max-w-sm items-center gap-1.5">
                    <Input
                      {...fieldProps}
                      type="file"
                      accept={UPLOAD_FILE_TYPES_ACCEPT}
                      onChange={(event) => onChange(event.target.files?.[0])}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("dashboard.createDocument.fileType")}</FormLabel>
                <FormControl>
                  <ControlledCombobox
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("commons.forms.selectAnOption")}
                    options={fileTypes}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <Button type="submit" disabled={creating}>
          {t("commons.buttons.create")}
        </Button>
        <div className="space-y-2 rounded-md border bg-gray-100 p-2 text-xs text-gray-700">
          {sourceWatch === "upload" && (
            <div>
              <p className="mb-1 font-medium">
                {t("dashboard.createDocument.supportedFormats")}
              </p>
              <p>
                {t("commons.fileFormat.word")}, {t("commons.fileFormat.excel")},{" "}
                {t("commons.fileFormat.powerPoint")},{" "}
                {t("commons.fileFormat.pdf")}
              </p>
            </div>
          )}
          <p>{t("dashboard.createDocument.fileTypeDescription")}</p>
        </div>
      </form>
    </Form>
  );
}
